import useApi from "@/hooks/useApi";
import useAccelaConfigs from "@/hooks/useAccelaConfigs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import Select, { SingleValue } from "react-select";
import AccelaPanelRow from "./AccelaPanelRow";
import CustomSelect from "../CustomSelect";
import { getScopeOptions } from "./AccelaScopeOptions";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import { AlertTriangle, IconClose } from "@velaro/velaro-component-lib";

export interface AccelaMap {
  id: number;
  accelaField: SingleValue<Option>;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  defaultValue: string;
  scopeType: string;
}

interface Option {
  value: any;
  label: string;
  key?: string;
  disabled?: boolean;
}

interface AccelaConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
}

interface Props {
  teamId: number;
  scopeType: string;
  showSavedMessage: () => void;
  isCreateMode?: boolean;
  selectedConfigId: number;
  isDeleteDisabled: boolean;
}

interface AccelaListItem {
  value: number;
  text: string;
}

export interface AccelaList {
  accelaField: string;
  listItems: Option[];
}

export default function AccelaPanel(props: Props) {
  const [mappedFields, setMappedFields] = useState<AccelaMap[]>([]);
  const [scopeType, setScopeType] = useState<string>(props.scopeType);
  const [selectedConfig, setSelectedConfig] = useState<number | undefined>(
    undefined
  );
  const [metadata, setMetadata] = useState<Option[]>([]);
  const [isLoading, setisLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [passedField, setPassedField] = useState<AccelaMap>();
  const { items, loading } = useAccelaConfigs(props.teamId);
  const [listFields, setListFields] = useState<AccelaList[] | null>(null);
  const api = useApi();
  const [expanded, setExpanded] = useState(true);
  const scopeOptions = getScopeOptions(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [scopeTypeToDelete, setScopeTypeToDelete] = useState<string | null>(null);
  const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{ index: number; fieldName: string } | null>(null);

  useEffect(() => {
    if (!loading) {
      setSelectedConfig(items[0]?.id);
    }
  }, [items, loading]);

  useEffect(() => {
    async function fetchMetadata() {
      if (!scopeType) {
        setMetadata([]);
        return;
      }
      setisLoading(true);

      if (props.selectedConfigId == null) return;

      const resp = await api.messaging.post(
        `Accela/Integrations/GetAccelaFields/${props.selectedConfigId}`,
        scopeType
      );

      const json = await resp.json();
      const newOptions = Object.values(json).map((key, i) => ({
        value: key,
        label: key
      })) as Option[];
      setMetadata(newOptions);
      setisLoading(false);
    }

    fetchMetadata();
  }, [api.messaging, scopeType, props.selectedConfigId]);

  const addRow = async () => {
    const data = [...mappedFields];

    const newMap: AccelaMap = {
      id: -1,
      accelaField: {
        value: "",
        label: ""
      },
      create: false,
      usedForSearching: false,
      showWhenSelected: false,
      showInSearch: false,
      defaultValue: "",
      scopeType: scopeType
    };
    data.push(newMap);
    setMappedFields(data);

    setTimeout(async () => {
      try {
        const resp = await api.messaging.get("Accela/Integrations/Mapping/");
        const lastId = await resp.json();

        setMappedFields((prevFields) =>
          prevFields.map((field) =>
            field.id === -1 ? { ...field, id: lastId } : field
          )
        );
      } catch (error) {
        console.error("Error fetching lastId:", error);
      }
    }, 1000);
  };

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    saveRecord();
  }, [mappedFields]);

  const handleDeleteFieldClick = (index: number) => {
    const field = mappedFields[index];
    const fieldName = field.accelaField?.label || 'this field';
    setFieldToDelete({ index, fieldName });
    setIsDeleteFieldModalOpen(true);
  };

  const confirmDeleteField = () => {
    if (fieldToDelete === null) return;

    const index = fieldToDelete.index;
    if (mappedFields[index].id !== -1) {
      deleteRecord(mappedFields[index].id);
    }
    const clone = [...mappedFields];
    clone.splice(index, 1);
    setMappedFields(clone);
    setIsDeleteFieldModalOpen(false);
    setFieldToDelete(null);
  };

  const fetchMapping = useCallback(async () => {
    if (props.selectedConfigId == undefined) return;
    const resp = await api.messaging.get(
      `Accela/Integrations/Mapping/${scopeType}/${props.selectedConfigId}`
    );

    if (!resp.ok) {
      console.error("Failed to fetch mapping data:", resp.statusText);
      return;
    }

    const json = await resp.json();

    json.forEach((element: { accelaField: { value: any; label: any } }) => {
      const value = {
        value: element.accelaField,
        label: element.accelaField
      };
      element.accelaField = value;
    });
    setMappedFields(json);
  }, [api.messaging, scopeType, props.selectedConfigId]);

  useEffect(() => {
    if (!scopeType) return;
    fetchMapping();
  }, [fetchMapping, scopeType]);

  function isOption(value: any): value is Option {
    return value && typeof value === "object" && "value" in value;
  }

  async function saveRecord() {
    const newMap: {
      accelaField: any;
      id: number;
      create: boolean;
      usedForSearching: boolean;
      showWhenSelected: boolean;
      showInSearch: boolean;
      defaultValue: string | SingleValue<Option>;
      scopeType: string;
    }[] = [];
    mappedFields.forEach((element) => {
      newMap.push({
        accelaField: element.accelaField?.value,
        id: element.id,
        create: element.create,
        usedForSearching: element.usedForSearching,
        showWhenSelected: element.showWhenSelected,
        showInSearch: element.showInSearch,
        defaultValue:
          element.defaultValue && isOption(element.defaultValue)
            ? element.defaultValue.value
            : element.defaultValue,
        scopeType: element.scopeType
      });
    });

    const response = await api.messaging.post(
      `Accela/Integrations/Mapping/${props.selectedConfigId}`,
      {
        Mappings: newMap
      }
    );
  }

  async function deleteRecord(id: number) {
    const response = await api.messaging.delete(
      `Accela/Integrations/Mapping/${id}`
    );
  }

  const handleDeleteRecordClick = (scopeType: string) => {
    setScopeTypeToDelete(scopeType);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteRecord = async () => {
    if (!scopeTypeToDelete) return;

    const response = await api.messaging.delete(
      `Accela/Integrations/Mapping/DeleteRecord/${scopeTypeToDelete}`
    );

    const updatedFields = mappedFields.filter(
      (field) => field.scopeType !== scopeTypeToDelete
    );
    setMappedFields(updatedFields);
    setIsDeleteModalOpen(false);
    setScopeTypeToDelete(null);
  };

  const updateMappedField = (index: number, mappedField: AccelaMap) => {
    const clone = [...mappedFields];
    clone[index] = mappedField;
    setMappedFields(clone);
  };

  function handleModal(passedField: AccelaMap) {
    setShowModal(true);
    setPassedField(passedField);
  }

  const fetchList = useCallback(
    async (passedFields: AccelaMap[]) => {
      const jsonArray = passedFields.map((passedField) => ({
        scopeType: passedField.scopeType,
        accelaField: passedField.accelaField?.value
      }));

      if (props.selectedConfigId == null) return;

      const resp = await api.messaging.post(
        `Accela/Integrations/GetAccelaFields/${props.selectedConfigId}`,
        {
          scopeType
        }
      );

      const json = await resp.json();
      const clone = [...(listFields || [])];

      passedFields.forEach((passedField) => {
        if (
          clone.filter(
            (item) => item.accelaField === passedField.accelaField?.value
          ).length > 0
        ) {
          clone.splice(
            clone.findIndex(
              (item) => item.accelaField === passedField.accelaField?.value
            ),
            1
          );
        }
        let scopeType = passedField.scopeType;

        const scopeTypeData = json[scopeType];
        const accelaFieldData = scopeTypeData?.[passedField.accelaField?.value];

        if (accelaFieldData) {
          const listItems = accelaFieldData.map((item: AccelaListItem) => ({
            value: item.value,
            label: item.text.replace(/&nbsp;/gi, "")
          }));

          clone.push({
            accelaField: passedField.accelaField?.value,
            listItems
          });
        }
      });
      setListFields(clone);
    },
    [api.messaging, listFields, props.selectedConfigId]
  );

  const ready = useMemo(() => !loading, [loading]);
  if (!ready) {
    return <></>;
  }

  let iconClassName = "transition fa-solid fa-angle-down";

  if (expanded) {
    iconClassName += " fa-rotate-180";
  }

  return (
    <div className="panel bg-slate-gray-50 dark:bg-stone-300 rounded-md shadow-md mb-4">
      <div
        className="panel-header flex items-center h-16 px-4 cursor-pointer"
        onClick={() => setExpanded((s) => !s)}
      >
        <div
          className="text-slate-50 dark:text-white font-medium text-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <CustomSelect
            options={scopeOptions}
            value={scopeType}
            width="w-72"
            onChange={setScopeType}
          />
        </div>

        <div
          className="ml-auto dark:text-white"
          onClick={() => setExpanded((s) => !s)}
        >
          <button
            className="h-8 w-8 text-slate-400"
            onClick={() => setExpanded((s) => !s)}
          >
            <i className={iconClassName}></i>
          </button>
        </div>
      </div>
      {expanded && (
        <div className="panel-body rounded-2xl animated fadeIn dark:border-stone-100 p-4">
          <table className="p-4 w-full bg-white">
            <thead>
              <tr>
                <th className="pb-4 p-2 pl-4 w-80 text-center">Accela Field</th>
                <th className="pb-4 p-2 w-80 text-center">Default Value</th>
                <th className="pb-4 p-2 w-24 text-center">Create</th>
                <th className="pb-4 p-2 w-24 text-center">
                  Used For Searching
                </th>
                <th className="pb-4 p-2 w-24 text-center">
                  Show When Selected
                </th>
                <th className="pb-4 p-2 w-24 text-center">Show In Search</th>
                <th className="pb-4 p-2 w-24 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {mappedFields.map((mappedField, i) => (
                <AccelaPanelRow
                  mappedField={mappedField}
                  i={i}
                  key={i}
                  listFields={listFields || []}
                  updateMappedField={updateMappedField}
                  isLoading={isLoading}
                  deleteRow={handleDeleteFieldClick}
                  handleModal={handleModal}
                  metadata={metadata}
                  fetchList={fetchList}
                  saveRecord={saveRecord}
                  showSavedMessage={props.showSavedMessage}
                />
              ))}
            </tbody>
          </table>

          {scopeType && (
            <div className="flex justify-end pt-4 gap-3">
              <button
                type="submit"
                className={`rounded-lg items-center px-6 py-2 border text-center text-sm font-medium ${
                  props.isDeleteDisabled || mappedFields.length === 0
                    ? "bg-slate-100 border-slate-100 text-slate-300 cursor-not-allowed"
                    : "bg-white border-cornflower-blue-500 hover:border-cornflower-blue-400 text-cornflower-blue-500"
                }`}
                onClick={() => handleDeleteRecordClick(scopeType)}
                disabled={props.isDeleteDisabled || mappedFields.length === 0}
              >
                Delete Record
              </button>

              <button
                type="submit"
                className="rounded-lg items-center px-6 py-2 bg-white rounded-lg border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-center text-cornflower-blue-500 text-sm font-medium"
                onClick={addRow}
              >
                Add Field
              </button>
            </div>
          )}
        </div>
      )}

      {isDeleteModalOpen && (
        <Modal show={isDeleteModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-slate-800 text-xl font-medium">
                  Delete Scope Type
                </div>
              </div>
              <button onClick={() => setIsDeleteModalOpen(false)}>
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>

            <div className="text-center text-slate-800 text-sm font-normal">
              Are you sure you want to delete all field mappings for{" "}
              <strong>{scopeTypeToDelete}</strong>? This action cannot be undone and will remove all mapped fields for this scope type.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="rounded-lg px-6 py-2 bg-white border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-[#1670e7] text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteRecord}
                className="rounded-lg px-6 py-2 bg-cornflower-blue-500 border border-cornflower-blue-500 hover:bg-cornflower-blue-400 hover:border-cornflower-blue-400 text-white text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}

      {isDeleteFieldModalOpen && (
        <Modal show={isDeleteFieldModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-slate-800 text-xl font-medium">
                  Delete Field Mapping
                </div>
              </div>
              <button onClick={() => setIsDeleteFieldModalOpen(false)}>
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>

            <div className="text-center text-slate-800 text-sm font-normal">
              Are you sure you want to delete the field mapping for{" "}
              <strong>{fieldToDelete?.fieldName}</strong>? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <button
                onClick={() => setIsDeleteFieldModalOpen(false)}
                className="rounded-lg px-6 py-2 bg-white border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-[#1670e7] text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteField}
                className="rounded-lg px-6 py-2 bg-cornflower-blue-500 border border-cornflower-blue-500 hover:bg-cornflower-blue-400 hover:border-cornflower-blue-400 text-white text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
